<template>
    <div>
        <!-- controls -->
        <div class="field is-horizontal">
            <div class="field-body">
                <div class="field">
                    <div class="control has-icons-left">
                        <input class="input"
                               type="text"
                               placeholder="Date From - To"
                               readonly
                               ref="date"
                               :disabled="loading">
                        <span class="icon is-small is-left">
                            <i class="fas fa-calendar-alt"></i>
                        </span>
                    </div>
                </div>
                <div class="field">
                    <p class="control is-expanded">
                        <div class="select is-fullwidth">
                            <select v-model="client"
                                    :disabled="loading"
                                    @change="load">
                                <option v-for="c in clients"
                                        :value="c" :key="c.id">{{c.name}}</option>
                            </select>
                        </div>
                    </p>
                </div>
                <div class="field">
                    <p class="control is-expanded">
                        <div class="select is-fullwidth">
                            <select v-model="method"
                                    :disabled="loading"
                                    @change="load">
                                <option value="Explicit">Explicit</option>
                                <option value="Pin">Pin</option>
                                <option value="Cashback">Cashback</option>
                                <option value="PaymentSystem">PaymentSystem</option>
                                <option value="Bonus">Bonus</option>
                            </select>
                        </div>
                    </p>
                </div>
                <div class="field">
                    <p class="control is-expanded">
                        <div class="select is-fullwidth">
                            <select v-model="currency"
                                    :disabled="loading"
                                    @change="load">
                                <option :value="null">All</option>
                                <option v-for="c in currencies" :key="c.id">{{c.id}}</option>
                            </select>
                        </div>
                    </p>
                </div>
            </div>
        </div>

        <!-- charts -->
        <p class="title is-4">Profit</p>
        <chart-profit ref="chartProfit"
                      :data="chartsData.profit" />
        
        <p class="title is-4">RTP</p>
        <chartRtp ref="chartRtp"
                      :data="chartsData.rtp" />

        <!-- summary -->
        <p class="title is-4">Total</p>
        <table class="table is-fullwidth">
            <tr>
                <th>Currency</th>
                <th>Deposit</th>
                <th>Withdraw</th>
                <th>Profit</th>
                <th>RTP</th>
            </tr>
            <tr v-for="t in stats.totals" :key="t.currency">
                <td>{{t.currency}}</td>
                <td>{{t.in | currency(t.currency)}}</td>
                <td>{{t.out | currency(t.currency)}}</td>
                <td :class="{'has-text-danger': t.in < t.out, 'has-text-success': t.in > t.out}">{{t.in - t.out | currency(t.currency)}}</td>
                <td>{{t.out / t.in  | rtp}}%</td>
            </tr>
        </table>

        <!-- grid -->
        <p class="title is-4">Details</p>
        <table class="table is-fullwidth">
            <tr>
                <th>Date</th>
                <th>Currency</th>
                <th>Deposit</th>
                <th>Withdraw</th>
                <th>Profit</th>
                <th>RTP</th>
            </tr>
            <template v-for="item in stats.items">
                <tr v-if="item.currencies.length"
                    v-for="(c,i) in item.currencies">
                    <td v-if="i == 0"
                        :rowspan="item.currencies.length">{{item.date | date}}</td>
                    <td>{{c.currency}}</td>
                    <td>{{c.in | currency(c.currency)}}</td>
                    <td>{{c.out | currency(c.currency)}}</td>
                    <td :class="{'has-text-danger': c.in < c.out, 'has-text-success': c.in > c.out}">{{c.in - c.out | currency(c.currency)}}</td>
                    <td>{{c.out / c.in | rtp}}%</td>
                </tr>
                <tr v-if="!item.currencies.length">
                    <td>{{item.date | date}}</td>
                    <td>-</td>
                    <td>0</td>
                    <td>0</td>
                    <td>0</td>
                    <td>-</td>
                </tr>
            </template>
        </table>
    </div>
</template>
<script>
import flatpickr from "flatpickr";
import state from "../../state";
import dt from "../../services/datetime.service";
import statsService from "../../services/stats.service";
import clientService from "../../services/client.service";
import currencyService from "../../services/currency.service";

import ChartProfit from "./chart-profit.vue";
import ChartRtp from "./chart-rtp.vue";

let getBlankChartsData = () => {
    return {
        profit: {
            currencies: []
        },
        rtp: {
            currencies: []
        }
    };
};

export default {
    name: "stats-profit",
    components: { ChartProfit, ChartRtp },
    data() {
        return {
            loading: false,
            calendar: null,
            client: null,
            method: "Pin",
            currency: null,
            stats: {
                items: [],
                totals: []
            },
            chartsData: getBlankChartsData()
        };
    },
    computed: {
        clients() {
            return state.clients.get();
        },
        currencies() {
            return state.currencies.get();
        }
    },
    watch: {},
    async mounted() {
        await clientService.ensure();
        this.client = this.clients[1];

        var self = this;

        let start = dt
            .local(new Date())
            .add(-6, "days")
            .format("YYYY-MM-DD");
        let end = dt.local(new Date()).format("YYYY-MM-DD");
        this.$data.calendar = flatpickr(this.$refs.date, {
            mode: "range",
            maxDate: "today",
            defaultDate: [start, end],
            onChange: function(selectedDates, dateStr, instance) {
                self.load();
            }
        });

        await this.load();
    },
    methods: {
        async load() {
            const start = dt.fixDate(this.$data.calendar.selectedDates[0]);
            const end = dt.fixDate(this.$data.calendar.selectedDates[1]);
            const from = dt
                .local(start)
                .utc()
                .toISOString();
            const to = dt
                .local(end)
                .endOf("day")
                .utc()
                .toISOString();

            let st = await statsService.getClientProfitStats(
                this.client.id,
                from,
                to,
                [this.method]
            );

            let daily = [];
            st.items.forEach(i => {
                let currencies = (i.methods[0] || { currencies: [] })
                    .currencies;
                if (this.currency)
                    currencies = currencies.filter(
                        c => c.currency == this.currency
                    );
                daily.push({
                    date: i.date,
                    currencies: currencies
                });
            });
            this.stats.items = daily;
            let cur = (st.totals[0] || { currencies: [] }).currencies;
            if (this.currency)
                cur = cur.filter(c => c.currency == this.currency);
            this.stats.totals = cur;

            // chart
            this.chartsData = getBlankChartsData();
            this.loadProfitChartData();
            this.loadRtpChartData();
        },
        loadProfitChartData() {
            let chartCurrencies = [];

            if (this.currency) {
                chartCurrencies = [ this.currency ];
            } else {
                this.stats.items.forEach(i => {
                    i.currencies.forEach(c => {
                        if (chartCurrencies.filter(cc => cc === c.currency).length <= 0) {
                            chartCurrencies.push(c.currency);
                        }
                    });
                });
            }

            chartCurrencies.forEach(chartCurrency => {
                var currencyData = {
                    currency: chartCurrency,
                    in: [],
                    out: [],
                    profit: []
                };
                this.chartsData.profit.currencies.push(currencyData);
                this.stats.items.forEach(i => {
                    let date = dt
                        .local(i.date)
                        .toDate()
                        .getTime();

                    var currencyStats = i.currencies.filter(c => c.currency === chartCurrency)[0] || { in: 0, out: 0 };

                    let inVal = currencyService.fromSource(currencyStats.in, chartCurrency);
                    currencyData.in.push([date, inVal]);

                    let outVal = currencyService.fromSource(currencyStats.out, chartCurrency);
                    currencyData.out.push([date, outVal]);

                    currencyData.profit.push([date, inVal - outVal]);
                });
            });
            setTimeout(() => {
                this.$refs.chartProfit.reload();
            });
        },
        loadRtpChartData() {
            let chartCurrencies = [];
            if (this.currency) {
                chartCurrencies = [ this.currency ];
            } else {
                this.stats.items.forEach(i => {
                    i.currencies.forEach(c => {
                        if (chartCurrencies.filter(cc => cc === c.currency).length <= 0) {
                            chartCurrencies.push(c.currency);
                        }
                    });
                });
            }
            chartCurrencies.forEach(chartCurrency => {
                var currencyData = {
                    currency: chartCurrency,
                    rtp: []
                };
                this.chartsData.rtp.currencies.push(currencyData);
                this.stats.items.forEach(i => {
                    let date = dt
                        .local(i.date)
                        .toDate()
                        .getTime();

                    var currencyStats = i.currencies.filter(
                        c => c.currency === chartCurrency
                    )[0] || { in: 0, out: 0 };
                    currencyData.rtp.push([date, Math.abs(currencyStats.in) < 1 ? 0 : parseFloat(((currencyStats.out / currencyStats.in) * 100).toFixed(2))]);
                });
            });
            setTimeout(() => {
                this.$refs.chartRtp.reload();
            });
        }
    }
};
</script>
<style>
</style>



// WEBPACK FOOTER //
// front/src/components/stats/profit.vue